import { useEffect } from 'react';
import useVehicleScheduleStore from '../stores/useVehicleScheduleStore';

/**
 * Mounted once at the page level. While a drag is active, attaches global
 * pointermove + pointerup + keydown listeners; uses elementFromPoint to find the
 * day cell under the pointer and dispatches updateDrag. On pointerup, commits via
 * the store (which PATCHes the event's ship_out_date / ship_back_date). Renders
 * nothing.
 *
 * Validity: a band edge edits the event's ship date, so the only illegal target
 * is one that crosses INTO the show span — a left (ship_out) drag must stay on or
 * before evStart, a right (ship_back) drag on or after evEnd.
 */
const VehicleDragController: React.FC = () => {
  const dragActive = useVehicleScheduleStore(s => !!s.drag);
  const updateDrag = useVehicleScheduleStore(s => s.updateDrag);
  const commitDrag = useVehicleScheduleStore(s => s.commitDrag);
  const cancelDrag = useVehicleScheduleStore(s => s.cancelDrag);

  useEffect(() => {
    if (!dragActive) return;

    const onMove = (e: PointerEvent) => {
      const drag = useVehicleScheduleStore.getState().drag;
      if (!drag) return;

      const target = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      if (!target) return;
      const cell = target.closest('[data-date][data-vehicle-id]') as HTMLElement | null;
      if (!cell) return;
      const date = cell.dataset.date!;
      const vehicleId = cell.dataset.vehicleId!;

      // Drag stays in the originating vehicle's row.
      if (vehicleId !== drag.vehicleId) return;

      // A left (ship_out) drag can't pass evStart; a right (ship_back) drag can't
      // pass evEnd — that would pull the leave/return inside the show.
      const invalid = drag.edge === 'left' ? date > drag.evStart : date < drag.evEnd;
      updateDrag(date, invalid);
    };

    const onUp = (e: PointerEvent) => {
      const target = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const cell = target?.closest('[data-date][data-vehicle-id]') ?? null;
      if (!cell) cancelDrag();
      else commitDrag();
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelDrag();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('keydown', onKey);
    };
  }, [dragActive, updateDrag, commitDrag, cancelDrag]);

  return null;
};

export default VehicleDragController;
